"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.formValidatorDecoratorService = void 0;
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const echain_shared_quantities_1 = require("@igus/echain-shared-quantities");
const NumberService_1 = require("./NumberService");
const RoundType_1 = require("./RoundType");
/**
 * Internal Implementation of the FormValidatorDecoratorService
 */
class _FormValidatorDecoratorService {
    /**
     * Extend the given validator function to a base validator function.
     * This step adds error handling functionality.
     *
     * @param validator The given validator function.
     * @param options
     * Options object:
     * errorKeys All possible errorKeys that the validator can create.
     * errorMessage A custom method that transform the given FormValidatorOutput to UI messages.
     * @returns Returns the given validator enriched with base validator functionality.
     */
    extendToBaseValidator(validator, options) {
        const baseFormValidator = validator;
        baseFormValidator.errorKeys = options.errorKeys;
        baseFormValidator.lastValidatorOutput = null;
        baseFormValidator.errorMessage = options.errorMessage;
        baseFormValidator.lastErrorKey = () => {
            const lastErrorKeys = baseFormValidator.lastErrorKeys();
            if (lastErrorKeys && lastErrorKeys.length > 0) {
                return lastErrorKeys[0];
            }
            return undefined;
        };
        // eslint-disable-next-line
        baseFormValidator.lastErrorValue = () => {
            const lastErrorKey = baseFormValidator.lastErrorKey();
            const lastValidationResult = baseFormValidator.lastValidatorOutput;
            if (lastErrorKey && lastValidationResult) {
                return lastValidationResult[lastErrorKey];
            }
            return undefined;
        };
        baseFormValidator.lastErrorKeys = () => {
            if (baseFormValidator.lastValidatorOutput) {
                const result = Object.keys(baseFormValidator.lastValidatorOutput);
                return result;
            }
            return undefined;
        };
        return baseFormValidator;
    }
    /**
     * Extend the given base validator to an observable validator.
     * An observable Validator provides observables for validation result changes and UI message changes.
     *
     * @param validator The given base validator.
     * @param option Options object:
     * errorKeys All possible errorKeys that the validator can create.
     * errorMessage A custom method that transform the given FormValidatorOutput to UI messages.
     * @returns Returns the given base validator enriched with observable validator functionality.
     */
    extendToObservableValidator(validator, options) {
        const validationChangesSubject = new rxjs_1.Subject();
        let formValidator = ((input) => {
            const result = validator(input);
            formValidator.lastValidatorOutput = result;
            validationChangesSubject.next(result);
            return result;
        });
        this.copyValidatorKeys(validator, formValidator);
        formValidator = this.extendToBaseValidator(formValidator, options);
        formValidator.validatorOutputChanges$ = validationChangesSubject.asObservable().pipe((0, operators_1.observeOn)(rxjs_1.asapScheduler));
        formValidator.errorMessageChanges$ = formValidator.validatorOutputChanges$.pipe((0, operators_1.map)((value) => formValidator.errorMessage(value, formValidator)));
        return formValidator;
    }
    /**
     * Extend the given base validator to an async observable validator.
     * An async observable Validator provides observables for validation result changes and UI message changes.
     * The difference between the observable and the async observable validator is, that the
     * async observable validator returns its validation result asynchronously.
     *
     * @param validator The given base validator.
     * @param option Options object:
     * errorKeys All possible errorKeys that the validator can create.
     * errorMessage A custom method that transform the given FormValidatorOutput to UI messages.
     * @returns Returns the given base validator enriched with async observable validator functionality.
     */
    extendToAsyncObservableValidator(validator, options) {
        const validationChangesSubject = new rxjs_1.Subject();
        let asyncFormValidator = ((input) => {
            let result = validator(input);
            if (result instanceof Promise) {
                result.then((value) => {
                    asyncFormValidator.lastValidatorOutput = value;
                    validationChangesSubject.next(value);
                });
                return result;
            }
            if ((0, rxjs_1.isObservable)(result)) {
                result = result.pipe((0, operators_1.tap)((value) => {
                    asyncFormValidator.lastValidatorOutput = value;
                    validationChangesSubject.next(value);
                }));
                return result;
            }
            return (0, rxjs_1.throwError)('unknown result type: ' + result);
        });
        this.copyValidatorKeys(validator, asyncFormValidator);
        asyncFormValidator = this.extendToBaseValidator(asyncFormValidator, options);
        asyncFormValidator.validatorOutputChanges$ = validationChangesSubject.asObservable().pipe((0, operators_1.observeOn)(rxjs_1.asapScheduler));
        asyncFormValidator.errorMessageChanges$ = asyncFormValidator.validatorOutputChanges$.pipe((0, operators_1.map)((value) => asyncFormValidator.errorMessage(value, asyncFormValidator)));
        return asyncFormValidator;
    }
    /**
     * Extend the given observable validator to a validator that provides helper methods for
     * the parsing of and transformation to localized number strings.
     * Precise rounding options can be defined for the validator.
     *
     * @param validator The given observable validator.
     * @param options Options object:
     * locale The locale that is used to interpret the number string.
     * roundType The round type that should be applied during the round operation.
     * The default round type is ROUND.
     * precision The desired number of digits after the decimal point that the rounded value should have.
     * The default value is 0.
     * significantPrecision The number of digits after the decimal point that are considered during rounding.
     * The default value is equal to precision + 1.
     * @returns Returns a localized number validator.
     */
    extendToLocalizedNumberValidator(validator, options) {
        const localizedNumberValidator = validator;
        localizedNumberValidator.locale = options.locale;
        localizedNumberValidator.roundType = options.roundType ? options.roundType : RoundType_1.RoundType.ROUND;
        localizedNumberValidator.precision = options.precision != null ? options.precision : 0;
        localizedNumberValidator.significantPrecision =
            options.significantPrecision != null ? options.significantPrecision : localizedNumberValidator.precision + 1;
        // eslint-disable-next-line
        localizedNumberValidator.toNumber = (value) => {
            return NumberService_1.numberService.toNumber(value, localizedNumberValidator.locale, localizedNumberValidator.roundType, localizedNumberValidator.precision, localizedNumberValidator.significantPrecision);
        };
        localizedNumberValidator.toLocalizedString = (value, hasGroupingSeparators) => {
            return NumberService_1.numberService.toLocalizedString(value, localizedNumberValidator.locale, hasGroupingSeparators, localizedNumberValidator.roundType, localizedNumberValidator.precision, localizedNumberValidator.significantPrecision);
        };
        return localizedNumberValidator;
    }
    /**
     * Extend the given localized threshold validator to a validator that provides helper methods for
     * the transformation to quantities.
     *
     * @param validator The given localized threshold validator.
     * @param options Options object:
     * unitSystem The unit system that is used.
     * unit The unit that should be used for the quantity.
     */
    extendToQuantityThresholdValidator(validator, options) {
        const quantityThresholdValidator = validator;
        quantityThresholdValidator.unitSystem = options.unitSystem;
        quantityThresholdValidator.unit = options.unit;
        // eslint-disable-next-line
        quantityThresholdValidator.toQuantity = (value) => {
            const parsedValue = quantityThresholdValidator.toNumber(value);
            return parsedValue || parsedValue === 0
                ? (0, echain_shared_quantities_1.Q)(parsedValue, quantityThresholdValidator.unit)
                : undefined;
        };
        quantityThresholdValidator.toQuantityValue = (quantity) => {
            return quantity === null || quantity === void 0 ? void 0 : quantity.getValue(quantityThresholdValidator.unit);
        };
        quantityThresholdValidator.toQuantityValueString = (quantity, hasGroupingSeparators) => {
            const quantityValue = quantity.getValue(quantityThresholdValidator.unit);
            return quantityValue || quantityValue === 0
                ? quantityThresholdValidator.toLocalizedString(quantityValue, hasGroupingSeparators)
                : undefined;
        };
        quantityThresholdValidator.toQuantityUnitString = () => {
            return echain_shared_quantities_1.Q.getUnitText(quantityThresholdValidator.unit);
        };
        quantityThresholdValidator.formatValue = function (quantity, hasGroupingSeparators = false, fallback = '') {
            var _a;
            return quantity ? (_a = this.toQuantityValueString(quantity, hasGroupingSeparators)) !== null && _a !== void 0 ? _a : fallback : fallback;
        };
        return quantityThresholdValidator;
    }
    /**
     * Extend the given localized range validator to a validator that provides helper methods for
     * the transformation to quantities.
     *
     * @param validator The given localized range validator.
     * @param options Options object:
     * unitSystem The unit system that is used.
     * unit The unit that should be used for the quantity.
     */
    extendToQuantityRangeValidator(validator, options) {
        const quantityRangeValidator = validator;
        quantityRangeValidator.unitSystem = options.unitSystem;
        quantityRangeValidator.unit = options.unit;
        // eslint-disable-next-line
        quantityRangeValidator.toQuantity = (value) => {
            const parsedValue = quantityRangeValidator.toNumber(value);
            return parsedValue || parsedValue === 0
                ? (0, echain_shared_quantities_1.Q)(parsedValue, quantityRangeValidator.unit)
                : undefined;
        };
        quantityRangeValidator.toQuantityValue = (quantity) => {
            return quantity === null || quantity === void 0 ? void 0 : quantity.getValue(quantityRangeValidator.unit);
        };
        quantityRangeValidator.toQuantityValueString = (quantity, hasGroupingSeparators) => {
            const quantityValue = quantity.getValue(quantityRangeValidator.unit);
            return quantityValue || quantityValue === 0
                ? quantityRangeValidator.toLocalizedString(quantityValue, hasGroupingSeparators)
                : undefined;
        };
        quantityRangeValidator.toQuantityUnitString = () => {
            return echain_shared_quantities_1.Q.getUnitText(quantityRangeValidator.unit);
        };
        return quantityRangeValidator;
    }
    /**
     * Copy the validator keys from one validator to another.
     *
     * @param fromValidatorFn The validator where the keys should be copied from.
     * @param toValidatorFn The validator where the keys should be copied to.
     */
    // eslint-disable-next-line
    copyValidatorKeys(fromValidatorFn, toValidatorFn) {
        for (const key in fromValidatorFn) {
            // eslint-disable-next-line
            if (fromValidatorFn.hasOwnProperty(key)) {
                toValidatorFn[key] = fromValidatorFn[key];
            }
        }
    }
}
/**
 * The exported validator decorator instance.
 *
 * @type {FormValidatorDecoratorService}
 */
exports.formValidatorDecoratorService = new _FormValidatorDecoratorService();
//# sourceMappingURL=FormValidatorDecoratorService.js.map